import { ISortByQuery } from '@common/api.models';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS, LOCAL_STORAGE_KEYS } from '@common/constants';
import { IFilter } from '@common/searchFilter/searchFilter.models';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  Card,
  Column,
  ComponentType,
  convertISOStringToLocalDateString,
  createTableCell,
  createTableColumn,
  Grid,
  Icon,
  Input,
  InputType,
  ResponsivePaginatedTable,
  TableRowExtras,
  Tooltip,
} from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import React, { useEffect, useState } from 'react';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import './ProjectList.scss';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { createTableLinkWithOpenNewTabColumn } from '@common/tableColumn.services';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { Link } from 'react-router-dom';
import { useFetchProjectSearch } from './project.queries';
import { IProject, projectSearchKeys } from './project.models';
import { ProjectListRowInterested } from './ProjectListRowInterested';
import { useShouldHideArchivedProjects } from './projectList.hooks';
import '@common/Common.scss';
import { LabelWithMessage } from '@common/LabelWithMessage';

export interface IProjectListListProps {}

export const ProjectList: React.FunctionComponent<IProjectListListProps> = ({}) => {
  const { shouldHideArchivedProjects, setShouldHideArchivedProjects } = useShouldHideArchivedProjects();
  const [localFilter, setLocalFilter] = React.useState<IFilter>({});
  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(
    EUserProfilePreferenceAttributeName.PROJECT_LIST_PAGE_SIZE,
  );
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>(undefined);
  const { navigate, generateRoutePath } = useNavigate();
  const [recentProjects, setRecentProjects] = useState<IProject[]>([]);

  useEffect(() => {
    const onFocus = () => {
      try {
        const newRecentProjects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.RECENT_PROJECTS) ?? '');
        setRecentProjects(newRecentProjects);
      } catch (error) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.RECENT_PROJECTS, '');
        setRecentProjects([]);
      }
    };
    onFocus();
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const {
    data: projectPagedResult,
    isLoading: isLoadingProjectSearch,
    isRefetching: isRefetchingProjectSearch,
    error: errorProjectSearch,
  } = useFetchProjectSearch(
    {
      pageSize,
      pageNumber,
      ...(sortByQuery || {}),
    },
    {
      keyword: localFilter[projectSearchKeys.PROJECT_NAME],
      isArchived: shouldHideArchivedProjects ? false : undefined,
    },
  );

  // #region Table Columns
  const projectTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableColumn({
        accessor: 'id2',
        Header: '',
        disableResizing: false,
        Cell: createTableCell<string, IProject>(
          (
            value: string,
            item: IProject,
            index: number,
            cellExtras: { [key: string]: any },
            rowExtras: TableRowExtras,
          ) => {
            const { rowState, setRowState } = rowExtras;
            return <ProjectListRowInterested project={item} rowState={rowState} setRowState={setRowState} />;
          },
        ),
      } as Column),
      createTableLinkWithOpenNewTabColumn<IProject>(
        projectSearchKeys.PROJECT_NO,
        'Project No.',
        ({ item }) => {
          return generateRoutePath(routes.home.project.path, { projectNo: item.projectNo }, false);
        },
        ({ value, item, index, cellExtras }, link) => {
          if (item.isArchived) {
            return (
              <Grid container>
                {link}
                <span className="gcl-gems-transmittal-list-tag gcl-ml-xs">
                  <Icon iconName="Archive" size={12} className="gcl-mr-xs" /> Archived
                </span>
              </Grid>
            );
          }
          return link;
        },
      ),
      createTableColumn({
        accessor: projectSearchKeys.PROJECT_NAME,
        Header: 'Project Name',
        Cell: createTableCell<string, IProject>((value: string, item: IProject) => {
          return (
            <LabelWithMessage
              className="gcl-gems-project-list-item-project-name"
              message={
                <Grid className="gcl-gems-project-list-item-project-name-tooltip" container spacing={1}>
                  {item.projectNo && (
                    <Grid item xs={12}>
                      <b>Project No.: {item.projectNo}</b>
                    </Grid>
                  )}
                  {item.projectName && (
                    <Grid item xs={12}>
                      <b>Project Name: {item.projectName}</b>
                    </Grid>
                  )}
                  {item.address && (
                    <Grid item xs={12}>
                      Address: {item.address}
                    </Grid>
                  )}
                  {item.description && (
                    <Grid item xs={12}>
                      Description: {item.description}
                    </Grid>
                  )}
                  {item.tel && (
                    <Grid item xs={12}>
                      Tel.: {item.tel}
                    </Grid>
                  )}
                  {item.fax && (
                    <Grid item xs={12}>
                      Fax: {item.fax}
                    </Grid>
                  )}
                  {item.email && (
                    <Grid item xs={12}>
                      Email: {item.email}
                    </Grid>
                  )}
                  {item.startDate && (
                    <Grid item xs={12}>
                      Start Date: {item.startDate ? convertISOStringToLocalDateString(item.startDate) : ''}
                    </Grid>
                  )}
                  {item.finishDate && (
                    <Grid item xs={12}>
                      Finish Date: {item.finishDate ? convertISOStringToLocalDateString(item.finishDate) : ''}
                    </Grid>
                  )}
                </Grid>
              }
              labelFor=""
              text={value}
            />
          );
        }),
      } as Column),
      // createTableColumn({
      //   accessor: projectSearchKeys.ADDRESS,
      //   Header: 'Address',
      //   disableSortBy: true,
      // } as Column),

      // createTableColumn({
      //   accessor: projectSearchKeys.DESCRIPTION,
      //   Header: 'Description',
      //   disableResizing: false,

      //   Cell: createTableCell<string, IProject>(
      //     (value: string, item: IProject, index: number, cellExtras: { [key: string]: any }) => {
      //       return (
      //         <Tooltip
      //           id="gcl-gems-project-list-item-desc-tooltip"
      //           overlay={<div className="gcl-gems-project-list-item-desc-tooltip">{item.description}</div>}
      //           placement="bottom"
      //         >
      //           <div className="gcl-txt-of-l1 gcl-txt-break">{item.description}</div>
      //         </Tooltip>
      //       );
      //     },
      //   ),
      // } as Column),

      // createTableColumn({
      //   accessor: projectSearchKeys.TEL,
      //   Header: 'Tel',
      //   disableSortBy: true,
      // } as Column),
      // createTableColumn({
      //   accessor: projectSearchKeys.FAX,
      //   Header: 'Fax',
      //   disableSortBy: true,
      // } as Column),
      // createTableColumn({
      //   accessor: projectSearchKeys.EMAIL,
      //   Header: 'Email',
      //   disableSortBy: true,
      // } as Column),

      // createTableColumn({
      //   accessor: projectSearchKeys.START_DATE,
      //   Header: 'Start Date',
      //   disableResizing: false,
      //   Cell: createTableCell<string, IProject>(
      //     (value: string, item: IProject, index: number, cellExtras: { [key: string]: any }) => {
      //       return <span>{item.startDate ? convertISOStringToLocalDateString(item.startDate) : ''}</span>;
      //     },
      //   ),
      // } as Column),
      // createTableColumn({
      //   accessor: projectSearchKeys.FINISH_DATE,
      //   Header: 'Finish Date',
      //   disableResizing: false,
      //   Cell: createTableCell<string, IProject>(
      //     (value: string, item: IProject, index: number, cellExtras: { [key: string]: any }) => {
      //       return <span>{item.finishDate ? convertISOStringToLocalDateString(item.finishDate) : ''}</span>;
      //     },
      //   ),
      // } as Column),
    ];
  }, []);
  // #endregion Table Columns
  return (
    <SearchPageBase
      isGlobalPage
      data={projectPagedResult?.items}
      breadcrumbItems={[
        {
          text: 'Home',
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.announcement.list.path);
          },
        },
        {
          text: `Search Project`,
          prefixIconName: 'Search',
          active: true,
        },
      ]}
      headerElement={
        recentProjects.length > 0 ? (
          <>
            <div className="gcl-gems-project-list-recent-projects">Recent Projects</div>
            <Grid container columnSpacing={2} className="gcl-mb-lg gcl-gems-project-list-recent-project-list">
              {(recentProjects ?? []).map((recentProject) => {
                return (
                  <Grid item xs={4} key={recentProject.projectNo}>
                    <Link
                      target="_blank"
                      to={generateRoutePath(routes.home.project.path, { projectNo: recentProject.projectNo }, false)}
                    >
                      <Card
                        className="gcl-gems-project-list-recent-project-card gcl-p-sm"
                        content={
                          <>
                            <div className="gcl-fw-600">{recentProject.projectNo}</div>
                            <div>{recentProject.projectName}</div>
                          </>
                        }
                      />
                    </Link>
                  </Grid>
                );
              })}
            </Grid>
          </>
        ) : undefined
      }
      title="Project List"
      isTableRefetching={isRefetchingProjectSearch}
      isTableLoading={isLoadingProjectSearch}
      buttonNavBarItem={[
        {
          text: 'Project Requests',
          onClick: () => {
            navigate(routes.projectRequest.path);
          },
          iconName: 'FileEarmark',
          componentType: ComponentType.Primary,
        },
        {
          text: 'New Project Request',
          onClick: () => {
            navigate(routes.projectRequest.create.path);
          },
          iconName: 'FileEarmarkPlus',
          componentType: ComponentType.Primary,
        },
      ]}
      buttonNavBarItemRight={[
        {
          component: shouldHideArchivedProjects ? (
            <Tooltip overlay="Show Archived Projects" id="showArchivedProjects">
              <Icon
                iconName="Eye"
                size={22}
                onClick={() => {
                  setShouldHideArchivedProjects(false);
                }}
                className="gcl-ic-btn-md"
              />
            </Tooltip>
          ) : (
            <Tooltip overlay="Hide Archived Projects" id="hideArchivedProjects">
              <Icon
                iconName="EyeSlash"
                size={22}
                onClick={() => {
                  setShouldHideArchivedProjects(true);
                }}
                className="gcl-ic-btn-md"
              />
            </Tooltip>
          ),
        },
      ]}
      appliedSearchFilter={localFilter}
      noContentLabel="No Project created yet"
      onRenderTableHeaderComponent={() => {
        return (
          <div className="gcl-mb-sm">
            <Input
              prefixLabel={{ prefixIconName: 'Search' }}
              placeholder="Search by Project Name"
              id={projectSearchKeys.PROJECT_NAME}
              type={InputType.Text}
              value={localFilter[projectSearchKeys.PROJECT_NAME]}
              onChange={(newValue) => {
                setLocalFilter({ [projectSearchKeys.PROJECT_NAME]: newValue?.toString() });
              }}
            />
          </div>
        );
      }}
      onRenderTable={() => {
        return (
          <>
            <ApiErrorDisplay errors={[{ error: errorProjectSearch, genericError: 'Get project list failed' }]} />
            <ResponsivePaginatedTable
              classNames={{ body: 'gcl-gems-list-table-body' }}
              columns={projectTableColumns}
              paginationProps={{
                pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
                paginatorPosition: 'bottom',
                totalCount: projectPagedResult?.totalCount || 0,
                initialPageSize: initPageSize,
                onPageChange: async (pageIndex: number, newPageSize: number) => {
                  setPageNumber(pageIndex + 1);
                  setPageSize(newPageSize);
                },
              }}
              sortingProps={{
                initialSortBy:
                  sortByQuery && sortByQuery.sortByField
                    ? [
                        {
                          id: sortByQuery.sortByField,
                          desc: !!sortByQuery.isDesc,
                        },
                      ]
                    : [],
                onSort: (sortBy) => {
                  if (sortBy && sortBy.length >= 1)
                    setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
                  else setSortByQuery(undefined);
                },
              }}
              allowSelectRow={false}
              items={projectPagedResult?.items || []}
              allowExpandRow={false}
            />
          </>
        );
      }}
    />
  );
};
